/*
 * mc-duet-upsell.js — Duet swap upsell (cart drawer + /cart page) + PostHog cart events.
 *
 * <duet-upsell> is rendered by snippets/duet-upsell.liquid when the cart holds a
 * 50mL line whose product has an in-stock DUET variant that is not already in the
 * cart. One tap swaps the 50mL line for the DUET variant of the same product:
 * add the DUET first, then zero the 50mL line — a failed add never removes the
 * item the customer already chose. Section rendering and the cartUpdate publish
 * mirror assets/cart-upsell.js (see that file for the drawer-vs-page split; the
 * page surface deliberately does NOT publish cartUpdate).
 *
 * PostHog (stub from snippets/posthog.liquid loads on idle; events queue until
 * posthog.capture exists, then flush):
 *   add_to_cart          { product_handle, variant_title, price } — every
 *                        /cart/add fetch sitewide. Chains onto the first-party
 *                        tracker's fetch wrapper in snippets/analytics-tracker.liquid.
 *   duet_upsell_shown    { product_handle } — once per product per page load,
 *                        when the row is actually visible (drawer open / page).
 *   duet_upsell_clicked  { product_handle } — on swap tap.
 *
 * Depends only on globals from global.js (routes, fetchConfig, publish,
 * PUB_SUB_EVENTS). Loaded with defer from layout/theme.liquid.
 */
(function () {
  'use strict';

  var queue = [];
  var flushTimer = null;
  var flushTries = 0;

  function posthogLive() {
    var p = window.posthog;
    return p && typeof p.capture === 'function' ? p : null;
  }

  function capture(name, props) {
    var p = posthogLive();
    if (p) {
      p.capture(name, props);
      return;
    }
    queue.push([name, props]);
    if (flushTimer) return;
    flushTimer = setInterval(function () {
      var live = posthogLive();
      if (live) {
        queue.splice(0).forEach(function (evt) {
          live.capture(evt[0], evt[1]);
        });
      }
      if (live || ++flushTries > 60) {
        clearInterval(flushTimer);
        flushTimer = null;
      }
    }, 500);
  }

  // add_to_cart on every successful /cart/add response, sitewide. The response
  // is the added line item (FormData/JSON posts) or { items: [...] } (items[] posts).
  var origFetch = window.fetch;
  window.fetch = function (input) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var result = origFetch.apply(this, arguments);
    if (url.indexOf('/cart/add') !== -1) {
      result
        .then(function (response) {
          if (!response.ok) return;
          response
            .clone()
            .json()
            .then(function (data) {
              var items = data.items || (data.handle ? [data] : []);
              items.forEach(function (item) {
                capture('add_to_cart', {
                  product_handle: item.handle,
                  variant_title: item.variant_title || item.title,
                  price: (item.final_price != null ? item.final_price : item.price) / 100
                });
              });
            })
            .catch(function () {});
        })
        .catch(function () {});
    }
    return result;
  };

  if (customElements.get('duet-upsell')) return;

  var shownHandles = {};

  customElements.define(
    'duet-upsell',
    class DuetUpsell extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('[data-swap]');
        this.errorEl = this.querySelector('[data-error]');
        if (this.button) this.button.addEventListener('click', this.onSwap.bind(this));
        this.trackShown();
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      // Fire duet_upsell_shown when the row is genuinely on screen: the drawer
      // markup exists (hidden) on every page, so intersection alone is not
      // enough — checkVisibility() filters the visibility:hidden closed state.
      trackShown() {
        var handle = this.dataset.handle;
        if (shownHandles[handle] || !('IntersectionObserver' in window)) return;
        var self = this;
        this.observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting || shownHandles[handle]) return;
              if (
                typeof self.checkVisibility === 'function' &&
                !self.checkVisibility({ visibilityProperty: true, opacityProperty: true })
              ) {
                return;
              }
              shownHandles[handle] = true;
              capture('duet_upsell_shown', { product_handle: handle });
              self.observer.disconnect();
            });
          },
          { threshold: 0.5 }
        );
        this.observer.observe(this);
      }

      setBusy(state) {
        this.classList.toggle('is-busy', state);
        if (!this.button) return;
        if (state) {
          this.button.setAttribute('disabled', '');
        } else {
          this.button.removeAttribute('disabled');
        }
      }

      showError() {
        this.setBusy(false);
        if (this.errorEl) this.errorEl.hidden = false;
      }

      onSwap() {
        if (this.classList.contains('is-busy')) return;
        var self = this;
        var ctx = this.dataset.context || 'drawer';
        var cartDrawer = document.querySelector('cart-drawer');
        var useDrawer = ctx === 'drawer' && cartDrawer;
        var quantity = parseInt(this.dataset.quantity, 10) || 1;
        var duetId = Number(this.dataset.variantId);
        var lineVariantId = Number(this.dataset.lineVariantId) || 0;
        // The DUET line we added, kept so a later failure can be rolled back.
        var addedLine = null;
        // Once the 50mL line is gone the swap is committed: from that point a
        // failure must never roll back or report "we couldn't update your bag".
        var swapCommitted = false;

        this.setBusy(true);
        if (this.errorEl) this.errorEl.hidden = true;
        capture('duet_upsell_clicked', { product_handle: this.dataset.handle });

        var pageTargets = useDrawer ? null : this.getPageTargets();
        var sectionNames = useDrawer
          ? cartDrawer.getSectionsToRender().map(function (section) {
              return section.id;
            })
          : pageTargets.map(function (target) {
              return target.name;
            });

        function postJSON(url, body) {
          var config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          config.body = body;
          return fetch(url, config).then(function (response) {
            return response.json();
          });
        }

        function removeBody(key) {
          return JSON.stringify({
            id: key,
            quantity: 0,
            sections: sectionNames,
            sections_url: window.location.pathname
          });
        }

        // Re-resolve the 50mL line against the live cart. A line key is
        // variantId:hash(properties), so ANY later rewrite of that line's
        // properties re-keys it and leaves the key baked into this markup
        // dead. snippets/sample-offers-block.liquid does exactly that after
        // add-to-cart (it stamps the complimentary-sample note onto the line)
        // without re-rendering this row, so the stale key would 400 with
        // "no valid id or line parameter". Match the baked key first, then
        // fall back to the line's variant, skipping gift lines.
        function resolveLineKey() {
          return fetch(routes.cart_url + '.js', { headers: { Accept: 'application/json' } })
            .then(function (response) {
              return response.json();
            })
            .then(function (cart) {
              var items = (cart && cart.items) || [];
              var baked = self.dataset.lineKey;
              var i;
              for (i = 0; i < items.length; i++) {
                if (items[i].key === baked) return baked;
              }
              for (i = 0; i < items.length; i++) {
                var props = items[i].properties || {};
                if (
                  items[i].variant_id === lineVariantId &&
                  !props._free_with &&
                  !props._free_choice_for
                ) {
                  return items[i].key;
                }
              }
              return null;
            });
        }

        postJSON(routes.cart_add_url, JSON.stringify({ id: duetId, quantity: quantity }))
          .then(function (added) {
            if (added.status) throw new Error(added.description || added.message);
            addedLine = added;
            return postJSON(routes.cart_change_url, removeBody(self.dataset.lineKey));
          })
          .then(function (state) {
            // Stale key — re-resolve against the live cart and retry once.
            if (!state.status) return state;
            return resolveLineKey().then(function (key) {
              if (!key || key === self.dataset.lineKey) {
                throw new Error(state.description || state.message);
              }
              return postJSON(routes.cart_change_url, removeBody(key));
            });
          })
          .then(function (state) {
            if (state.status) throw new Error(state.description || state.message);
            swapCommitted = true;
            try {
              if (useDrawer) {
                cartDrawer.renderContents(state);
                // Ecosystem sync (cart-icon bubble, guarded item refetch) — same
                // publish contract as cart-upsell.js.
                if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                  publish(PUB_SUB_EVENTS.cartUpdate, {
                    source: 'duet-upsell',
                    cartData: state,
                    variantId: duetId
                  });
                }
              } else {
                self.renderPageSections(state.sections, pageTargets);
              }
            } catch (renderError) {
              // The bag IS the Duet now. A rendering fault must not masquerade
              // as a failed swap; reload so the UI matches the real cart.
              console.error('duet-upsell render failed:', renderError);
              window.location.reload();
            }
          })
          .catch(function (error) {
            console.error('duet-upsell swap failed:', error);
            // The DUET add already committed but the 50mL line is still there.
            // Undo it so the shopper is never left paying for both.
            if (!swapCommitted && addedLine && addedLine.key) {
              var restore = Math.max(0, (addedLine.quantity || quantity) - quantity);
              postJSON(
                routes.cart_change_url,
                JSON.stringify({ id: addedLine.key, quantity: restore })
              )
                .catch(function () {})
                .then(function () {
                  self.showError();
                });
              return;
            }
            self.showError();
          });
      }

      // /cart page render targets — section ids from data-id, NOT DOM ids
      // (same contract as cart-upsell.js getPageTargets).
      getPageTargets() {
        var targets = [];
        var itemsEl = document.getElementById('main-cart-items');
        var footerEl = document.getElementById('main-cart-footer');
        var bubbleEl = document.getElementById('cart-icon-bubble');
        if (itemsEl) targets.push({ name: itemsEl.dataset.id, el: itemsEl, selector: '.js-contents' });
        if (footerEl) targets.push({ name: footerEl.dataset.id, el: footerEl, selector: '.js-contents' });
        if (bubbleEl) targets.push({ name: 'cart-icon-bubble', el: bubbleEl, selector: '.shopify-section' });
        return targets;
      }

      renderPageSections(sections, targets) {
        if (!sections || !targets) return;
        targets.forEach(function (target) {
          var html = sections[target.name];
          if (!html || !target.el) return;
          var source = new DOMParser().parseFromString(html, 'text/html').querySelector(target.selector);
          if (!source) return;
          if (target.name === 'cart-icon-bubble') {
            target.el.innerHTML = source.innerHTML;
          } else {
            var dest = target.el.querySelector(target.selector);
            if (dest) dest.innerHTML = source.innerHTML;
          }
        });
      }
    }
  );
})();
